import { useEffect, useRef, useState } from 'react'
import { CAL_EVENT, calUrl } from '../../lib/links'
import { useLocale } from '../../i18n/LocaleContext'

/**
 * Cal.com's official loader snippet, kept verbatim as a string rather than
 * transcribed to TypeScript.
 *
 * It is not optional and it is not just sugar: `embed.js` throws
 * "Cal is not defined" unless `window.Cal.q` already exists when it runs. The
 * snippet installs that queueing stub synchronously, appends the script on the
 * FIRST `Cal(...)` call (so importing this module costs nothing on pages that
 * never render the calendar), and the real implementation drains the queue on
 * load. Keeping it byte-identical to what Cal.com publishes means a future
 * upstream change can be diffed instead of re-derived.
 */
const LOADER =
  '(function (C, A, L) { let p = function (a, ar) { a.q.push(ar); }; let d = C.document; ' +
  'C.Cal = C.Cal || function () { let cal = C.Cal; let ar = arguments; if (!cal.loaded) { ' +
  'cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; ' +
  'cal.loaded = true; } if (ar[0] === L) { const api = function () { p(api, arguments); }; ' +
  'const namespace = ar[1]; api.q = api.q || []; if (typeof namespace === "string") { ' +
  'cal.ns[namespace] = cal.ns[namespace] || api; p(cal.ns[namespace], ar); ' +
  'p(cal, ["initNamespace", namespace]); } else p(cal, ar); return; } p(cal, ar); }; })' +
  '(window, "https://app.cal.com/embed/embed.js", "init");'

type CalApi = (...args: unknown[]) => void
type CalGlobal = CalApi & { ns?: Record<string, CalApi> }

declare global {
  interface Window {
    Cal?: CalGlobal
  }
}

/** Appends the loader once per document. Safe to call on every mount. */
function ensureLoader(): CalGlobal | null {
  if (typeof window === 'undefined') return null
  if (!window.Cal) {
    const s = document.createElement('script')
    s.dataset.calLoader = 'true'
    s.textContent = LOADER
    document.head.appendChild(s)
  }
  return window.Cal ?? null
}

/**
 * If neither `linkReady` nor `linkFailed` has fired by now, treat the embed as
 * unavailable and show the plain link. Covers the cases Cal.com cannot report
 * itself — script blocked by an extension, offline, CDN down.
 */
const READY_TIMEOUT_MS = 12_000

type Status = 'loading' | 'ready' | 'failed'

/**
 * Cal.com inline booking calendar, rendered inside the page — no redirect to a
 * third-party domain, which is the entire reason we left Calendly.
 *
 * `namespace` isolates one instance from another: the contact page mounts this
 * twice (the "book a call" tab and the last step of the quote wizard), and Cal
 * keys its instances by namespace. Two mounts sharing one namespace would fight
 * over the same iframe.
 */
export default function CalEmbed({
  namespace,
  active = true,
  name,
  email,
  onBooked,
  loadingLabel,
  fallbackLabel,
  minHeight = 620,
}: {
  namespace: string
  /**
   * Whether the calendar's container is currently on screen.
   *
   * The contact page keeps both tab panels mounted so the quote wizard does not
   * lose the visitor's answers when they switch tabs — but an iframe inside a
   * `display:none` parent measures itself as zero-height and comes back broken.
   * So the embed is torn down while its panel is hidden and rebuilt on return.
   */
  active?: boolean
  /** Prefills the booking form. Empty strings are omitted, not sent blank. */
  name?: string
  email?: string
  onBooked?: () => void
  loadingLabel: string
  fallbackLabel: string
  minHeight?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [status, setStatus] = useState<Status>('loading')
  // One duplicated Cal.com event type per language — see CAL_EVENT.
  const locale = useLocale()
  const calLink = CAL_EVENT[locale]
  const fallbackUrl = calUrl(locale)

  // `onBooked` is usually an inline arrow from the parent, so a new identity on
  // every render. Held in a ref so the effect below is not re-run — re-running
  // it would tear down and rebuild the iframe mid-booking.
  const onBookedRef = useRef(onBooked)
  onBookedRef.current = onBooked

  useEffect(() => {
    const el = ref.current
    if (!el || !active) return
    const Cal = ensureLoader()
    if (!Cal) {
      setStatus('failed')
      return
    }

    // React 18 StrictMode runs effects twice in dev; Cal appends a fresh
    // <cal-inline> each time it is asked to. Start from an empty container so a
    // remount never stacks two calendars.
    el.innerHTML = ''
    setStatus('loading')

    Cal('init', namespace, { origin: 'https://cal.com' })
    const api = Cal.ns?.[namespace]
    if (!api) {
      setStatus('failed')
      return
    }

    const onReady = () => setStatus('ready')
    const onFailed = () => setStatus('failed')
    const onSuccess = () => onBookedRef.current?.()

    api('inline', {
      elementOrSelector: el,
      calLink,
      config: {
        layout: 'month_view',
        // The page it sits on is light; without this Cal follows the visitor's
        // OS preference and drops a dark calendar into a white card.
        theme: 'light',
        ...(name ? { name } : {}),
        ...(email ? { email } : {}),
      },
    })

    api('ui', {
      layout: 'month_view',
      hideEventTypeDetails: false,
      cssVarsPerTheme: { light: { 'cal-brand': '#242fd6' } },
    })

    api('on', { action: 'linkReady', callback: onReady })
    api('on', { action: 'linkFailed', callback: onFailed })
    api('on', { action: 'bookingSuccessful', callback: onSuccess })

    const timer = window.setTimeout(
      () => setStatus((s) => (s === 'loading' ? 'failed' : s)),
      READY_TIMEOUT_MS,
    )

    return () => {
      window.clearTimeout(timer)
      api('off', { action: 'linkReady', callback: onReady })
      api('off', { action: 'linkFailed', callback: onFailed })
      api('off', { action: 'bookingSuccessful', callback: onSuccess })
      el.innerHTML = ''
    }
  }, [namespace, name, email, active, calLink])

  return (
    <div>
      <div className="relative w-full" style={{ minHeight }}>
        <div
          ref={ref}
          className="w-full overflow-hidden rounded-2xl"
          style={{ minHeight, visibility: status === 'ready' ? 'visible' : 'hidden' }}
        />

        {active && status === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-2xl border border-ink-200 bg-white/60">
            <span
              aria-hidden
              className="h-6 w-6 animate-spin rounded-full border-2 border-accent-200 border-t-accent-600"
            />
            <p className="text-sm text-ink-500">{loadingLabel}</p>
          </div>
        )}

        {active && status === 'failed' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-2xl border border-ink-200 bg-white/60 px-6 text-center">
            <p className="max-w-sm text-sm leading-relaxed text-ink-600">{fallbackLabel}</p>
            <a
              href={fallbackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-accent-700 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-800"
            >
              {fallbackUrl.replace('https://', '')}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                <path d="M7 17L17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
